import { ReactNode, Component, PureComponent, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import moment from 'moment';
import * as WidgetsThingsStructs from '../WidgetsThingsStructs';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { AdvancedInfoTip, AdvancedInfoTipAjvSchema, createPortStateFromInfoTipEvent, InfoTipItem } from '@Primitive/InfoTips';
import { DebugEx } from '@Utils/DebugEx';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { WebThing, WebThingPort } from '@API/WebThing';
import { PortData, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import type { CalendarEvent, CalendarLegendItem } from '@Primitive/Calendar';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { RamStorage } from '@Utils/RamStorage';

const PrimitiveCalendar = lazy(() => import('@Primitive/Calendar'));
// =================================================================================================
export interface CalendarData {
    Views: ViewInfo[];
    Selectable: boolean;
    Legend: {
        Show: boolean;
        Items: CalendarLegendItem[];
    },
    MobileThreshold: number;
}

interface ViewInfo {
    key: 'month' | 'week' | 'work_week' | 'day' | 'agenda';
    enabled: boolean;
    name: string;
    threshold: number;
}

// -------------------------------------------------------------------------------------------------
export interface CalendarState {
    events: CalendarEvent[];
    legendItems: CalendarLegendItem[];
    /**
     * Use it to configure whether a week start from Monday
     */
    weekStartsFromMonday?: boolean;
}
// -------------------------------------------------------------------------------------------------
interface SavedState {
    defaultView: 'month' | 'week' | 'work_week' | 'day' | 'agenda';
}
// -------------------------------------------------------------------------------------------------
class Calendar extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<CalendarData>, CalendarState> {
    // ---------------------------------------------------------------------------------------------
    private _stateStorage: RamStorage;
    private _savedState: SavedState;
    // ---------------------------------------------------------------------------------------------
    private _prevPortTS: number;
    private _dataVersion: number;
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<CalendarData>) {
        super(props);
        this._dataVersion = 0;
        // init storage
        this._stateStorage = RamStorage.GetInstance();
        this._savedState = this._stateStorage.GetState(this.props.ThingKey) as SavedState;
        if (!isDefined(this._savedState))
            this._savedState = { defaultView: null };

        this.state = {
            events: [],
            legendItems: null,
            weekStartsFromMonday: false
        };
    }
    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        this._UpdateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<CalendarData>) {
        this._UpdateData(nextProps);

        const { Legend } = nextProps.WidgetData;
        if (!_.isEqual(Legend, this.props.WidgetData.Legend)) {
            this.setState({ legendItems: this._getLegendItems(Legend) });
        }
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        const { width, WidgetData } = this.props;
        const { events, legendItems, weekStartsFromMonday } = this.state;
        let views = [];
        _.forEach(WidgetData.Views, (view) => {
            if (!view.enabled)
                return;
            if (view.threshold == 0 || view.threshold <= width)
                views.push(view.key);
        });

        if (views.length > 0 &&
            (!isDefined(this._savedState) ||
                views.indexOf(this._savedState.defaultView) === -1)
        )
            this._updateStorage(views[0]);

        return <Suspense fallback={<LazyLoadingAnimation />}>
            <PrimitiveCalendar
                key={this._dataVersion}
                localizationKey={this.props.NodeKey}
                views={views}
                defaultView={this._savedState.defaultView}
                onView={this._updateStorage}
                width={width}
                events={events}
                weekStartsFromMonday={weekStartsFromMonday}
                onSelect={WidgetData.Selectable ? this._onSelect : null}
                onPopupDataChange={this._onPopupDataChange}
                legend={{ items: legendItems }}
                mobileView={WidgetData.MobileThreshold > width}
            />
        </Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private _onSelect = (ev) => {
        if (!isDefined(this.props.SendPortEvent))
            return;
        this.props.SendPortEvent(PortKeys.select, JSON.stringify(ev));
    }
    // ---------------------------------------------------------------------------------------------
    private _onPopupDataChange = (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => {
        if (!isDefined(this.props.SendPortEvent))
            return;
        this.props.SendPortEvent(PortKeys.popup,
            createPortStateFromInfoTipEvent(i, infoTip, iconClicked));
    }
    // ---------------------------------------------------------------------------------------------
    private _updateStorage = (view) => {
        // increase `_dataVersion`; we need to destroy the primitive...
        this._dataVersion++;
        this._savedState.defaultView = view;
        this._stateStorage.SetState(this.props.ThingKey, this._savedState);
    }
    // ---------------------------------------------------------------------------------------------
    private _UpdateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<CalendarData>) {
        //try get `PortsStates`
        if (!isDefined(props.PortsStates))
            return;

        _.forEach([PortKeys.data, PortKeys.legend, PortKeys.weekStartFromMonday], (portKey) => {

            // try get 'data' port
            const port = props.PortsStates[portKey];
            if (!isDefined(port))
                return;

            if (!this.processPort(port))
                return;

            if (portKey == PortKeys.data) {

                // try get port state
                let data: CalendarEvent[];
                if (port.LastStateStatus == StateStatus.NoData) {
                    // fallback to default data
                    data = _.cloneDeep(props.WidgetDefaultValues[portKey]);
                    if (!isDefined(data))
                        data = [];
                } else if (port.LastStateStatus == StateStatus.Valid) {

                    // try get data from port state
                    try {
                        data = _.cloneDeep(port.State);

                        // convert to date strings to date objects; else the inner library will throw an exception!
                        _.forEach(data, (el) => {
                            if (isDefined(el.start))
                                el.start = moment(el.start).toDate();

                            if (el.allDay == true)
                                el.end = _.clone(el.start)
                            else if (isDefined(el.end))
                                el.end = moment(el.end).toDate();
                        });
                    }
                    catch (ex) {
                        DebugEx.TraceException(ex);
                    }
                }
                else
                    // nothing to do
                    return;

                this._dataVersion++;
                this.setState({ events: data });
            }
            else if (portKey == PortKeys.legend) {

                // try get port state
                let legendItems: CalendarLegendItem[];
                if (port.LastStateStatus == StateStatus.NoData) {
                    // try fallback to default data
                    legendItems = _.cloneDeep(props.WidgetDefaultValues[portKey]);
                    if (!isDefined(legendItems)) {
                        // fallback to settings
                        legendItems = this._getLegendItems(props.WidgetData.Legend);
                    }
                } else if (port.LastStateStatus == StateStatus.Valid) {

                    // try get data from port state
                    legendItems = _.cloneDeep(port.State);
                }
                else
                    // nothing to do
                    return;

                this._dataVersion++;
                this.setState({ legendItems: legendItems });
            } else if (portKey == PortKeys.weekStartFromMonday) {
                let weekStartsFromMonday: boolean = _.cloneDeep(port.State);
                this.setState({ weekStartsFromMonday: weekStartsFromMonday });
            }

            // update timestamp cache
            const nextTS = port.Timestamp;
            if (isDefined(nextTS))
                this._prevPortTS = nextTS;
        });
    }
    // --------------------------------------------------------------------------------------------
    private processPort(port: PortData): boolean {
        // Compare timestamps of states.
        return !isDefined(this._prevPortTS) || this._prevPortTS < port.Timestamp;
    }
    // ---------------------------------------------------------------------------------------------
    private _getLegendItems(legend: { Show: boolean, Items: CalendarLegendItem[] }): CalendarLegendItem[] {
        return !legend.Show ? null : legend.Items;
    }
    // --------------------------------------------------------------------------------------------
}
// =================================================================================================
class CalendarSettings extends PureComponent<IWidgetSettingsProps<CalendarData>> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<CalendarData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<CalendarData>[] = [
            {
                name: '',
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Selectable',
                        tooltip: 'If enabled, on user selection an event will be fired.',
                        dataPath: 'Selectable'
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Views',
                        tooltip: 'You can enable and specify the views order. You can also, set a minimum width threshold for handling small screens. Set to 0 for disabling this feature. Available views: Month, Week, Work week, Day and Agenda.',
                        dataPath: 'Views',
                        readonly: true,
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: 'Name',
                                dataPath: 'name',
                                isEnabled: () => false
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Enabled',
                                dataPath: 'enabled'
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Width threshold in pixels',
                                dataPath: 'threshold',
                                maxValue: 2600,
                                minValue: 0,
                                valuePosfix: 'px',
                                stepValue: 1,
                                isEnabled: (data: CalendarData, row: ViewInfo) => row.enabled
                            }
                        ],

                    },
                    {
                        type: SettingsType.Group,
                        name: 'Legend',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Show legend',
                                tooltip: 'If enabled, legend information will displayed in top toolbar. An information icon with a popup will be used.',
                                dataPath: 'Legend.Show'
                            },
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                dialogStaticSize: { w: 0.7, h: 0.8 },
                                label: 'Legend Items',
                                tooltip: 'Legend\'s items.',
                                dataPath: 'Legend.Items',
                                itemsInfo: [
                                    {
                                        type: SettingsType.String,
                                        label: 'Label',
                                        dataPath: 'label'
                                    },
                                    {
                                        type: SettingsType.PaletteColorPicker,
                                        label: 'Palette Color',
                                        dataPath: 'colorClassName'
                                    }
                                ],
                                createNewItem: () => {
                                    return { label: '', colorClassName: '' }
                                }
                            },
                        ]
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Width mobile threshold in pixels',
                        dataPath: 'MobileThreshold',
                        maxValue: 2000,
                        minValue: 200,
                        valuePosfix: 'px',
                        stepValue: 1,
                    },
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================

// lazy replace
const enum PortKeys {
    data = 'data',
    select = 'select',
    popup = 'popup',
    legend = 'legend',
    weekStartFromMonday = 'weekStartFromMonday'
}
export class CalendarWidget extends IWidgetThingsClass<CalendarData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <Calendar />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <CalendarSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: 'Data',
                PortKey: PortKeys.data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.data, data))
        );


        ports.push(new WebThingPort(
            {
                Name: 'Week Start From Monday',
                PortKey: PortKeys.weekStartFromMonday,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        const { Selectable, Legend } = this.WidgetData;
        if (Selectable)
            ports.push(new WebThingPort(
                {
                    Name: 'Selected event',
                    PortKey: PortKeys.select,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ''
                },
                WebThingPortType.Trigger)
            );

        ports.push(new WebThingPort(
            {
                Name: 'On Popup Data Change',
                PortKey: PortKeys.popup,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        if (Legend.Show)
            ports.push(new WebThingPort(
                {
                    Name: 'Legend',
                    PortKey: PortKeys.legend,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(PortKeys.legend, data))
            );

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): CalendarData {
        return {
            Selectable: false,
            Views: [
                {
                    key: 'month',
                    name: 'Month',
                    enabled: true,
                    threshold: 1000
                },
                {
                    key: 'work_week',
                    name: 'Work week',
                    enabled: true,
                    threshold: 800
                },
                {
                    key: 'week',
                    name: 'Week',
                    enabled: true,
                    threshold: 1000
                },
                {
                    key: 'day',
                    name: 'Day',
                    enabled: true,
                    threshold: 0
                },
                {
                    key: 'agenda',
                    name: 'Agenda',
                    enabled: true,
                    threshold: 0
                },
            ],
            Legend: {
                Show: false,
                Items: []
            },
            MobileThreshold: 600
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return CalendarWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Calendar',
            WidgetTitle: '',
            Key: 'CalendarWidget',
            Type: 'CalendarWidget',
            Data: CalendarWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/calendar.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.data)
            return CalendarDataItemsJsonSchemeValidator().DataValidateString(value);
        if (key == PortKeys.legend)
            return CalendarLegendItemsJsonSchemeValidator().DataValidateString(value);

        return { valid: true, errors: '' };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: CalendarData): CalendarData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return { [PortKeys.data]: [] }; }
    // ---------------------------------------------------------------------------------------------
}
// ---------------------------------------------------------------------------------------------
function CalendarDataItemsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance(
        'CalendarDataItemsJsonSchemeValidator',
        {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: { type: ['string', 'number'] },
                    title: { type: 'string' },
                    colorClassName: { type: 'string' },
                    desc: { type: 'string' },
                    allDay: { type: 'boolean', },
                    start: { type: 'string' },
                    end: { type: 'string' },
                    infotip: AdvancedInfoTipAjvSchema
                },
                if: { properties: { allDay: { type: 'boolean', } } },
                then: { required: ['id', 'title', 'start'] },
                else: { required: ['id', 'title', 'start', 'end'] },
            },
            minItems: 0
        }
    );
}
// ---------------------------------------------------------------------------------------------
function CalendarLegendItemsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance(
        'CalendarLegendItemsJsonSchemeValidator',
        {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    colorClassName: { type: 'string' },
                    label: { type: 'string' },
                },
                required: ['colorClassName', 'label']
            },
            minItems: 0
        }
    );
}
// -------------------------------------------------------------------------------------------------
